import { z } from 'zod';
import { BID_ORIGINS, BID_SOURCES, BID_TYPES, CODE_FAMILIES, CONDITION_GRADES, CONFIDENCE_LEVELS, CONSENT_ACTIONS, CONSENT_CHANNELS, CONSENT_TYPES, DEALER_STATUSES, DEAL_OUTCOMES, DEAL_STATUSES, DECODE_SOURCE_STATUSES, DECODE_SOURCE_TYPES, DISCLOSURE_STATES, FALLOUT_REASONS, FEEDBACK_PARTIES, INTERACTION_OUTCOMES, INTERACTION_TYPES, LIEN_STATUSES, MEDIA_KINDS, MESSAGE_CHANNELS, OFFER_STATUSES, OPERATOR_ROLES, OPTION_CATEGORIES, OPTION_SOURCES, PAINT_TO_SAMPLE_PREFS, PASS_REASONS, PIPELINE_STAGES, PREFERRED_CONTACT_METHODS, PRICE_PRIORITIES, RETAIL_EXPECTATION_RISKS, REVEAL_TYPES, REVIEW_TASK_KINDS, REVIEW_TASK_PRIORITIES, REVIEW_TASK_STATUSES, SELLER_RESPONSES, SPEC_SOURCES, SUBMISSION_SOURCES, SUBMISSION_STATUSES, SUBJECT_TYPES, TESTIMONIAL_ATTRIBUTIONS, TIER_CONTEXTS, TITLE_STATUSES, TRANSMISSIONS, TRANSMISSION_CONFIDENCES, TRIGGER_REASONS, } from './enums.js';
/** E.164 phone (`+14155552671`). */
export const e164 = z
    .string()
    .regex(/^\+[1-9]\d{6,14}$/, 'must be E.164, e.g. +14155552671');
/**
 * USPS two-letter codes — the 50 states + DC, plus territories and military codes (a safe
 * superset; L2/M18). Keeping the runtime check as a refine (not z.enum) preserves the
 * `string` inferred type every consumer already carries.
 */
const US_STATE_CODES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY',
    'DC', // federal district
    'PR', 'GU', 'VI', 'AS', 'MP', // territories
    'AA', 'AE', 'AP', // military
];
const US_STATE_SET = new Set(US_STATE_CODES);
/** US two-letter state code (uppercased; must be a real USPS state/DC/territory code — L2). */
export const usState = z
    .string()
    .length(2)
    .toUpperCase()
    .refine((s) => US_STATE_SET.has(s), { message: 'must be a valid two-letter US state code' });
/**
 * 17-character VIN (uppercased) restricted to the legal VIN alphabet — no I/O/Q and, since
 * `raw_vin` is interpolated into outbound decode-provider requests, no URL metacharacters
 * either (L2/M18). Shape-only; the ISO-3779 check digit is evaluated at intake (core vin.ts).
 */
export const vin = z
    .string()
    .toUpperCase()
    .regex(/^[A-HJ-NPR-Z0-9]{17}$/, 'must be a 17-character VIN (letters except I/O/Q, digits)');
/** Money in integer cents (architecture invariant: no floats, ever). */
export const cents = z.number().int();
export const isoTimestamp = z.string().datetime({ offset: true });
export const uuid = z.string().uuid();
// ---------------------------------------------------------------------------
// Core entities (read/response shapes)
// ---------------------------------------------------------------------------
export const operatorSchema = z.object({
    id: uuid,
    email: z.string().email(),
    name: z.string(),
    role: z.enum(OPERATOR_ROLES),
    is_active: z.boolean(),
    last_login_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const dealerSchema = z.object({
    id: uuid,
    business_name: z.string(),
    state: usState,
    status: z.enum(DEALER_STATUSES),
    subscription_tier: z.string().nullable(),
    network_notes: z.string().nullable(),
    created_by: uuid.nullable(),
    // M9 pipeline enrichment.
    relationship_owner_id: uuid.nullable(),
    relationship_owner_name: z.string().nullable(),
    pipeline_stage: z.enum(PIPELINE_STAGES),
    preferred_contact_method: z.enum(PREFERRED_CONTACT_METHODS).nullable(),
    response_sla_hours: z.number().int().nullable(),
    // M15/M5 — identity disambiguators (name+state is not a real-world identity).
    city: z.string().nullable(),
    external_ref: z.string().nullable(),
    /** Refined model — official Porsche franchise store (the launch network; only franchise dealers are blasted). */
    franchise: z.boolean(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
/** M9 — a logged founder touch (call/email/text/note) + its inline follow-up reminder. */
export const dealerInteractionSchema = z.object({
    id: uuid,
    dealer_id: uuid,
    dealer_contact_id: uuid.nullable(),
    operator_id: uuid.nullable(),
    type: z.enum(INTERACTION_TYPES),
    outcome: z.enum(INTERACTION_OUTCOMES).nullable(),
    notes: z.string().nullable(),
    occurred_at: isoTimestamp,
    follow_up_at: isoTimestamp.nullable(),
    follow_up_note: z.string().nullable(),
    follow_up_done: z.boolean(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const dealerContactSchema = z.object({
    id: uuid,
    dealer_id: uuid,
    name: z.string().nullable(),
    phone_e164: e164.nullable(),
    email: z.string().email().nullable(),
    role_title: z.string().nullable(),
    is_primary: z.boolean(),
    timezone: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const dealerIsoPrefSchema = z.object({
    id: uuid,
    dealer_id: uuid,
    label: z.string(),
    active: z.boolean(),
    models: z.array(z.string()),
    generations: z.array(z.string()),
    year_min: z.number().int().nullable(),
    year_max: z.number().int().nullable(),
    transmission: z.array(z.string()),
    mileage_max: z.number().int().nullable(),
    budget_max_cents: cents.nullable(),
    exterior_colors: z.array(z.string()),
    paint_to_sample: z.enum(PAINT_TO_SAMPLE_PREFS),
    required_option_codes: z.array(z.string()),
    preferred_option_codes: z.array(z.string()),
    has_named_buyer: z.boolean(),
    criteria_json: z.record(z.unknown()),
    notes: z.string().nullable(),
    created_by: uuid.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const sellerSubmissionSchema = z.object({
    id: uuid,
    public_ref: z.string(),
    source: z.enum(SUBMISSION_SOURCES),
    seller_name: z.string().nullable(),
    seller_phone_e164: e164.nullable(),
    seller_email: z.string().email().nullable(),
    seller_state: usState.nullable(),
    seller_timezone: z.string().nullable(),
    seller_region_label: z.string().nullable(),
    raw_vin: z.string().nullable(),
    vehicle_id: uuid.nullable(),
    stated_mileage: z.number().int().nullable(),
    trade_target_cents: cents.nullable(),
    asking_price_cents: cents.nullable(),
    condition_grade: z.enum(CONDITION_GRADES).nullable(),
    title_status: z.enum(TITLE_STATUSES).nullable(),
    accident_disclosed: z.boolean().nullable(),
    has_modifications: z.boolean().nullable(),
    condition_notes: z.string().nullable(),
    status: z.enum(SUBMISSION_STATUSES),
    consent_id: uuid.nullable(),
    assigned_operator_id: uuid.nullable(),
    intake_notes: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
    withdrawn_at: isoTimestamp.nullable(),
});
/**
 * M10 — the SELLER-EXPECTATION + readiness qualification for a submission (1:1, optional). Its presence
 * means "this seller has been qualified"; its absence means "not yet qualified". Read shape returned on
 * the submission detail + the liquidity control tower.
 */
export const submissionQualificationSchema = z.object({
    id: uuid,
    submission_id: uuid,
    sell_motivation: z.string().nullable(),
    seller_floor_cents: cents.nullable(),
    price_priority: z.enum(PRICE_PRIORITIES).nullable(),
    accepts_dealer_direct: z.boolean().nullable(),
    retail_expectation_risk: z.enum(RETAIL_EXPECTATION_RISKS).nullable(),
    ownership_confirmed: z.boolean().nullable(),
    lien_status: z.enum(LIEN_STATUSES).nullable(),
    ready_to_transact: z.boolean().nullable(),
    qualification_notes: z.string().nullable(),
    qualified_by: uuid.nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const vehicleSchema = z.object({
    id: uuid,
    vin: vin,
    vin_valid: z.boolean(),
    make: z.string().nullable(),
    model: z.string().nullable(),
    model_year: z.number().int().nullable(),
    series_generation: z.string().nullable(),
    trim: z.string().nullable(),
    body_class: z.string().nullable(),
    engine_cylinders: z.number().int().nullable(),
    displacement_l: z.number().nullable(),
    engine_hp: z.number().int().nullable(),
    plant_city: z.string().nullable(),
    plant_country: z.string().nullable(),
    decode_status: z.string(),
    primary_spec_source: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const consentSchema = z.object({
    id: uuid,
    subject_type: z.enum(SUBJECT_TYPES),
    subject_id: uuid,
    phone_e164: e164.nullable(),
    email: z.string().email().nullable(),
    channel: z.enum(CONSENT_CHANNELS),
    consent_type: z.enum(CONSENT_TYPES),
    action: z.enum(CONSENT_ACTIONS),
    capture_source: z.string(),
    consent_text_snapshot: z.string(),
    consent_text_version: z.string(),
    evidence_object_key: z.string().nullable(),
    ip_address: z.string().nullable(),
    user_agent: z.string().nullable(),
    captured_at: isoTimestamp,
    recorded_by: uuid.nullable(),
    created_at: isoTimestamp,
});
export const stateSmsRuleSchema = z.object({
    state: usState,
    sms_window_start: z.string(),
    sms_window_end: z.string(),
    primary_timezone: z.string(),
    advertising_only: z.boolean(),
    mini_tcpa: z.boolean(),
    notes: z.string().nullable(),
});
export const bidSchema = z.object({
    id: uuid,
    bid_origin: z.enum(BID_ORIGINS),
    blast_id: uuid.nullable(),
    blast_recipient_id: uuid.nullable(),
    dealer_id: uuid,
    vehicle_id: uuid,
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.nullable(),
    trade_description: z.string().nullable(),
    trade_allowance_cents: cents.nullable(),
    pass_reason: z.enum(PASS_REASONS).nullable(),
    pass_reason_text: z.string().nullable(),
    conditions: z.string().nullable(),
    is_binding: z.boolean(),
    status: z.string(),
    source: z.enum(BID_SOURCES),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const dealSchema = z.object({
    id: uuid,
    seller_submission_id: uuid,
    vehicle_id: uuid,
    winning_offer_id: uuid.nullable(),
    winning_bid_id: uuid.nullable(),
    dealer_id: uuid,
    agreed_price_cents: cents.nullable(),
    tier_context: z.enum(TIER_CONTEXTS),
    status: z.enum(DEAL_STATUSES),
    outcome: z.enum(DEAL_OUTCOMES).nullable(),
    outcome_reason: z.string().nullable(),
    closed_price_cents: cents.nullable(),
    closed_at: isoTimestamp.nullable(),
    // M12 close-out + learning loop (all nullable; the deal core keeps its absence of fee/funds/title).
    fallout_reason: z.enum(FALLOUT_REASONS).nullable(),
    seller_confirmed: z.boolean().nullable(),
    dealer_confirmed: z.boolean().nullable(),
    seller_confirmed_at: isoTimestamp.nullable(),
    dealer_confirmed_at: isoTimestamp.nullable(),
    testimonial_permission: z.boolean().nullable(),
    testimonial_attribution: z.enum(TESTIMONIAL_ATTRIBUTIONS).nullable(),
    testimonial_quote: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
/** M12 — one side's NPS (0–10) + comment on a deal (seller or dealer). Both sides are captured. */
export const dealFeedbackSchema = z.object({
    id: uuid,
    deal_id: uuid,
    party: z.enum(FEEDBACK_PARTIES),
    nps: z.number().int().min(0).max(10).nullable(),
    comment: z.string().nullable(),
    collected_by: uuid.nullable(),
    collected_at: isoTimestamp,
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
/**
 * A curated offer (the seller menu row). TIER-1 INVARIANT: `presented_amount_cents` == the source
 * `bids.amount_cents` (strict pass-through — a neutral venue takes no spread). Staged disclosure lives
 * in `disclosure_state`: 'masked' until the seller accepts (→ 'dealer_revealed') / contact releases.
 */
export const offerSchema = z.object({
    id: uuid,
    seller_submission_id: uuid,
    bid_id: uuid,
    presented_amount_cents: cents,
    rank: z.number().int().nullable(),
    status: z.enum(OFFER_STATUSES),
    seller_response: z.enum(SELLER_RESPONSES).nullable(),
    disclosure_state: z.enum(DISCLOSURE_STATES),
    presented_at: isoTimestamp.nullable(),
    responded_at: isoTimestamp.nullable(),
    expires_at: isoTimestamp.nullable(),
    created_at: isoTimestamp,
});
/** An audited identity/contact reveal (the structural staged-disclosure record). */
export const disclosureEventSchema = z.object({
    id: uuid,
    offer_id: uuid.nullable(),
    deal_id: uuid.nullable(),
    bid_id: uuid.nullable(),
    reveal_type: z.enum(REVEAL_TYPES),
    trigger_reason: z.enum(TRIGGER_REASONS),
    triggered_by: uuid.nullable(),
    created_at: isoTimestamp,
});
// ---------------------------------------------------------------------------
// M2 decode — vehicle spec, options, option-code dictionary, sources, review tasks
// ---------------------------------------------------------------------------
export const vehicleSpecSchema = z.object({
    id: uuid,
    vehicle_id: uuid,
    model_designation: z.string().nullable(),
    exterior_color: z.string().nullable(),
    exterior_color_code: z.string().nullable(),
    paint_to_sample: z.boolean(),
    pts_color_name: z.string().nullable(),
    interior_color: z.string().nullable(),
    interior_material: z.string().nullable(),
    transmission: z.enum(TRANSMISSIONS),
    transmission_confidence: z.enum(TRANSMISSION_CONFIDENCES),
    base_msrp_cents: cents.nullable(),
    options_msrp_cents: cents.nullable(),
    total_msrp_cents: cents.nullable(),
    spec_source: z.enum(SPEC_SOURCES).nullable(),
    confidence: z.enum(CONFIDENCE_LEVELS),
    verified_by_operator: z.boolean(),
    resolved_from_source_id: uuid.nullable(),
    spec_fingerprint: z.string().nullable(),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const vehicleOptionSchema = z.object({
    id: uuid,
    vehicle_id: uuid,
    raw_code: z.string(),
    normalized_code: z.string(),
    option_dictionary_id: uuid.nullable(),
    description_snapshot: z.string().nullable(),
    option_msrp_cents: cents.nullable(),
    is_package: z.boolean(),
    is_high_value: z.boolean(),
    source: z.enum(OPTION_SOURCES),
    created_at: isoTimestamp,
});
export const optionCodeSchema = z.object({
    id: uuid,
    code: z.string(),
    code_family: z.enum(CODE_FAMILIES).nullable(),
    description: z.string(),
    category: z.enum(OPTION_CATEGORIES).nullable(),
    applies_generations: z.array(z.string()),
    applies_models: z.array(z.string()),
    year_min: z.number().int().nullable(),
    year_max: z.number().int().nullable(),
    is_high_value: z.boolean(),
    aliases: z.array(z.string()),
    source_refs: z.array(z.string()),
    confidence: z.enum(CONFIDENCE_LEVELS),
    created_at: isoTimestamp,
    updated_at: isoTimestamp,
});
export const decodeSourceSchema = z.object({
    id: uuid,
    vehicle_id: uuid,
    source_type: z.enum(DECODE_SOURCE_TYPES),
    provider: z.string().nullable(),
    object_key: z.string().nullable(),
    cost_cents: z.number().int(),
    status: z.enum(DECODE_SOURCE_STATUSES),
    requested_by: uuid.nullable(),
    created_at: isoTimestamp,
});
export const reviewTaskSchema = z.object({
    id: uuid,
    kind: z.enum(REVIEW_TASK_KINDS),
    entity_type: z.string(),
    entity_id: uuid,
    status: z.enum(REVIEW_TASK_STATUSES),
    priority: z.enum(REVIEW_TASK_PRIORITIES),
    detail: z.record(z.unknown()),
    created_at: isoTimestamp,
    resolved_at: isoTimestamp.nullable(),
});
// ---------------------------------------------------------------------------
// Input DTOs (validated at the API edge)
// ---------------------------------------------------------------------------
/** Public seller intake (POST /public/submissions). */
export const createSubmissionInput = z.object({
    source: z.enum(SUBMISSION_SOURCES).default('web_form'),
    seller_name: z.string().min(1).max(200).optional(),
    seller_phone_e164: e164.optional(),
    seller_email: z.string().email().optional(),
    seller_state: usState.optional(),
    raw_vin: vin,
    stated_mileage: z.number().int().nonnegative().max(1_000_000).optional(),
    trade_target_cents: cents.nonnegative().optional(),
    asking_price_cents: cents.nonnegative().optional(),
    condition_grade: z.enum(CONDITION_GRADES).optional(),
    title_status: z.enum(TITLE_STATUSES).optional(),
    accident_disclosed: z.boolean().optional(),
    has_modifications: z.boolean().optional(),
    condition_notes: z.string().max(5000).optional(),
    // PEWC gate — a submission that intends marketing SMS must carry explicit consent.
    pewc_consent: z.boolean().default(false),
});
/** Operator / dealer bid entry (POST /bids, POST /public/bid/:token). */
export const createBidInput = z
    .object({
    bid_origin: z.enum(BID_ORIGINS).default('blast'),
    blast_id: uuid.optional(),
    blast_recipient_id: uuid.optional(),
    dealer_id: uuid,
    vehicle_id: uuid,
    bid_type: z.enum(BID_TYPES),
    amount_cents: cents.positive().optional(),
    trade_description: z.string().optional(),
    trade_allowance_cents: cents.optional(),
    pass_reason: z.enum(PASS_REASONS).optional(),
    pass_reason_text: z.string().optional(),
    conditions: z.string().optional(),
    source: z.enum(BID_SOURCES),
})
    .refine((b) => b.bid_type === 'no_bid' || b.bid_type === 'pass' || b.amount_cents != null, {
    message: 'priced bids (cash/trade) require amount_cents',
    path: ['amount_cents'],
})
    .refine((b) => b.bid_origin !== 'blast' || (b.blast_id != null && b.blast_recipient_id != null), {
    message: 'blast-origin bids must carry blast_id and blast_recipient_id',
    path: ['blast_id'],
});
/**
 * Operator manual intake (POST /api/submissions). Superset of the public form: the operator
 * may set coarse location, timezone, notes, and an assignee. `source` is forced to
 * 'operator_manual'; a truthy `pewc_consent` records consent with capture_source 'operator_manual'.
 */
export const operatorCreateSubmissionInput = createSubmissionInput.extend({
    source: z.literal('operator_manual').default('operator_manual'),
    seller_timezone: z.string().max(64).optional(),
    seller_region_label: z.string().max(120).optional(),
    intake_notes: z.string().max(5000).optional(),
    assigned_operator_id: uuid.optional(),
    pewc_consent: z.boolean().default(false),
});
/** Operator edit (PATCH /api/submissions/:id) — condition covariates, workflow status, notes, assignee. */
export const updateSubmissionInput = z
    .object({
    condition_grade: z.enum(CONDITION_GRADES).nullable(),
    title_status: z.enum(TITLE_STATUSES).nullable(),
    accident_disclosed: z.boolean().nullable(),
    has_modifications: z.boolean().nullable(),
    condition_notes: z.string().max(5000).nullable(),
    intake_notes: z.string().max(5000).nullable(),
    seller_region_label: z.string().max(120).nullable(),
    seller_timezone: z.string().max(64).nullable(),
    stated_mileage: z.number().int().nonnegative().max(1_000_000).nullable(),
    trade_target_cents: cents.nonnegative().nullable(),
    asking_price_cents: cents.nonnegative().nullable(),
    assigned_operator_id: uuid.nullable(),
    status: z.enum(SUBMISSION_STATUSES),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/**
 * M10 — upsert a submission's seller-expectation qualification (PUT /api/submissions/:id/qualification).
 * Every field is optional (a qualification is captured incrementally over a call); at least one is
 * required. `null` explicitly clears a field. This is the seller's OWN attestation — the packet score
 * and launch-gate status are derived elsewhere, never set here.
 */
export const upsertQualificationInput = z
    .object({
    sell_motivation: z.string().max(2000).nullable(),
    seller_floor_cents: cents.nonnegative().nullable(),
    price_priority: z.enum(PRICE_PRIORITIES).nullable(),
    accepts_dealer_direct: z.boolean().nullable(),
    retail_expectation_risk: z.enum(RETAIL_EXPECTATION_RISKS).nullable(),
    ownership_confirmed: z.boolean().nullable(),
    lien_status: z.enum(LIEN_STATUSES).nullable(),
    ready_to_transact: z.boolean().nullable(),
    qualification_notes: z.string().max(5000).nullable(),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one qualification field is required' });
/** Seller-media upload kind (photo | window_sticker) — the two intake media kinds. */
export const uploadMediaKind = z.enum(['photo', 'window_sticker']).default('photo');
export const mediaSummarySchema = z.object({
    id: uuid,
    owner_type: z.string(),
    owner_id: uuid,
    kind: z.enum(MEDIA_KINDS),
    object_key: z.string(),
    exif_stripped: z.boolean(),
    content_type: z.string().nullable(),
    byte_size: z.number().int().nullable(),
    created_at: isoTimestamp,
});
/** Channel-agnostic compliance-gate request (shared shape; used by api + worker). */
export const gateRequestSchema = z.object({
    channel: z.enum(MESSAGE_CHANNELS),
    to_e164: e164.optional(),
    email: z.string().email().optional(),
    subject_state: usState.optional(),
    purpose: z.enum(['marketing', 'transactional', 'opt_out_confirm']).default('marketing'),
});
/**
 * Operator spec override (PATCH /api/vehicles/:id/spec). Any field an operator corrects wins over
 * every decode source (precedence: operator > paid_api > seller_sticker > vpic). Setting any field
 * flips verified_by_operator=true and re-resolves the spec_fingerprint.
 */
export const updateVehicleSpecInput = z
    .object({
    model_designation: z.string().min(1).max(80).nullable(),
    exterior_color: z.string().max(120).nullable(),
    exterior_color_code: z.string().max(40).nullable(),
    paint_to_sample: z.boolean(),
    pts_color_name: z.string().max(120).nullable(),
    interior_color: z.string().max(120).nullable(),
    interior_material: z.string().max(120).nullable(),
    transmission: z.enum(TRANSMISSIONS),
    transmission_confidence: z.enum(TRANSMISSION_CONFIDENCES),
    base_msrp_cents: cents.nonnegative().nullable(),
    options_msrp_cents: cents.nonnegative().nullable(),
    total_msrp_cents: cents.nonnegative().nullable(),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/** Dictionary resolve query (GET /api/dictionary/resolve?code=&generation=&year=). */
export const dictionaryResolveQuery = z.object({
    code: z.string().min(1).max(16),
    generation: z.string().min(1).max(16).optional(),
    year: z.coerce.number().int().min(1950).max(2100).optional(),
});
// ---------------------------------------------------------------------------
// M3 dealer network — input DTOs (validated at the API edge)
// ---------------------------------------------------------------------------
/**
 * M9 pipeline-enrichment fields — optional on create/onboard/import, patchable on update.
 * `relationship_owner_id` FKs to an operator ("which founder knows this dealer");
 * `relationship_owner_name` is the free-text fallback for a CSV-imported name that isn't an operator.
 */
const dealerPipelineFields = {
    relationship_owner_id: uuid.optional(),
    relationship_owner_name: z.string().min(1).max(120).optional(),
    pipeline_stage: z.enum(PIPELINE_STAGES).optional(),
    preferred_contact_method: z.enum(PREFERRED_CONTACT_METHODS).optional(),
    response_sla_hours: z.number().int().min(0).max(2160).optional(),
};
/** Dealer (company) mutable fields. `state` drives the NJ rule + fallback timing window. */
const dealerFields = {
    business_name: z.string().min(1).max(200),
    state: usState,
    status: z.enum(DEALER_STATUSES).optional(),
    subscription_tier: z.string().min(1).max(80).optional(),
    network_notes: z.string().max(5000).optional(),
    // M15/M5 — identity disambiguators: two same-name dealers in one state are distinct businesses.
    city: z.string().min(1).max(120).optional(),
    external_ref: z.string().min(1).max(120).optional(),
    /** Refined model — official Porsche franchise store. Only franchise dealers receive blasts (DB default false). */
    franchise: z.boolean().optional(),
    ...dealerPipelineFields,
};
/** POST /api/dealers */
export const createDealerInput = z.object(dealerFields);
/** PATCH /api/dealers/:id — any subset; null clears a nullable text/pipeline column. */
export const updateDealerInput = z
    .object({
    business_name: z.string().min(1).max(200),
    state: usState,
    status: z.enum(DEALER_STATUSES),
    subscription_tier: z.string().max(80).nullable(),
    network_notes: z.string().max(5000).nullable(),
    relationship_owner_id: uuid.nullable(),
    relationship_owner_name: z.string().max(120).nullable(),
    pipeline_stage: z.enum(PIPELINE_STAGES),
    preferred_contact_method: z.enum(PREFERRED_CONTACT_METHODS).nullable(),
    response_sla_hours: z.number().int().min(0).max(2160).nullable(),
    // M15/M5 — backfillable identity disambiguators (null clears).
    city: z.string().max(120).nullable(),
    external_ref: z.string().max(120).nullable(),
    /** Refined model — mark/unmark the dealer as an official Porsche franchise store (the blast network). */
    franchise: z.boolean(),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/**
 * A dealer CONTACT (a number/email). Consent + opt-out attach HERE, never to the company —
 * the TCPA-correct grain. A contact requires at least a phone or an email (mirrors the DB CHECK).
 */
const dealerContactFields = {
    name: z.string().min(1).max(200).optional(),
    phone_e164: e164.optional(),
    email: z.string().email().optional(),
    role_title: z.string().min(1).max(120).optional(),
    is_primary: z.boolean().optional(),
    timezone: z.string().min(1).max(64).optional(),
};
const contactHasAddress = (c) => Boolean(c.phone_e164 || c.email);
/** POST /api/dealers/:id/contacts */
export const createDealerContactInput = z
    .object(dealerContactFields)
    .refine(contactHasAddress, { message: 'a contact requires at least a phone_e164 or an email', path: ['phone_e164'] });
/** PATCH /api/dealer-contacts/:id — provided fields only (nulling a required address is not allowed). */
export const updateDealerContactInput = z
    .object(dealerContactFields)
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/** B2B dealer consent (implied_business or opt_in), captured for a specific CONTACT. */
const dealerConsentFields = {
    channel: z.enum(CONSENT_CHANNELS).default('sms'),
    consent_type: z.enum(['implied_business', 'opt_in']).default('implied_business'),
    action: z.enum(CONSENT_ACTIONS).default('granted'),
    capture_source: z.string().min(1).max(120).default('dealer_onboarding'),
};
/** POST /api/dealers/:id/consent — capture/record consent for one of the dealer's contacts. */
export const dealerConsentInput = z.object({ contact_id: uuid, ...dealerConsentFields });
/** A contact + its (optional) consent, captured together during onboarding. */
export const onboardDealerContactInput = z
    .object({
    ...dealerContactFields,
    consent: z
        .object({
        channel: z.enum(CONSENT_CHANNELS).default('sms'),
        consent_type: z.enum(['implied_business', 'opt_in']).default('implied_business'),
        capture_source: z.string().min(1).max(120).default('dealer_onboarding'),
    })
        .optional(),
})
    .refine(contactHasAddress, { message: 'a contact requires at least a phone_e164 or an email', path: ['phone_e164'] });
/** POST /api/dealers/onboard — create a dealer + its contacts + each contact's consent atomically. */
export const dealerOnboardInput = z.object({
    ...dealerFields,
    contacts: z.array(onboardDealerContactInput).min(1).max(20),
});
/**
 * `dealer_iso_prefs` — the in-search-of spec preferences, the matching INPUT and the wedge signal.
 * `transmission` restricts to the two real gearboxes (never 'unknown' as a REQUEST). `has_named_buyer`
 * is THE WEDGE: a buyer-in-hand can pay near-retail.
 */
const isoPrefBase = {
    active: z.boolean().optional(),
    models: z.array(z.string().min(1).max(60)).max(50).optional(),
    generations: z.array(z.string().min(1).max(20)).max(50).optional(),
    year_min: z.number().int().min(1950).max(2100),
    year_max: z.number().int().min(1950).max(2100),
    transmission: z.array(z.enum(['manual', 'pdk'])).max(2).optional(),
    mileage_max: z.number().int().nonnegative().max(1_000_000),
    budget_max_cents: cents.nonnegative(),
    exterior_colors: z.array(z.string().min(1).max(60)).max(50).optional(),
    paint_to_sample: z.enum(PAINT_TO_SAMPLE_PREFS).optional(),
    required_option_codes: z.array(z.string().min(1).max(16)).max(50).optional(),
    preferred_option_codes: z.array(z.string().min(1).max(16)).max(50).optional(),
    has_named_buyer: z.boolean().optional(),
    criteria_json: z.record(z.unknown()).optional(),
    notes: z.string().max(5000).optional(),
};
const yearOrder = (o) => o.year_min == null || o.year_max == null || o.year_min <= o.year_max;
/** POST /api/dealers/:id/iso-prefs */
export const createIsoPrefInput = z
    .object({
    label: z.string().min(1).max(120),
    ...isoPrefBase,
    year_min: isoPrefBase.year_min.optional(),
    year_max: isoPrefBase.year_max.optional(),
    mileage_max: isoPrefBase.mileage_max.optional(),
    budget_max_cents: isoPrefBase.budget_max_cents.optional(),
})
    .refine(yearOrder, { message: 'year_min must be <= year_max', path: ['year_min'] });
/** PATCH /api/iso-prefs/:id — any subset; null clears a numeric bound. */
export const updateIsoPrefInput = z
    .object({
    label: z.string().min(1).max(120),
    ...isoPrefBase,
    year_min: isoPrefBase.year_min.nullable(),
    year_max: isoPrefBase.year_max.nullable(),
    mileage_max: isoPrefBase.mileage_max.nullable(),
    budget_max_cents: isoPrefBase.budget_max_cents.nullable(),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' })
    .refine(yearOrder, { message: 'year_min must be <= year_max', path: ['year_min'] });
/** GET /api/dealers — list filter/pagination. */
export const dealerListQuery = z.object({
    status: z.enum(DEALER_STATUSES).optional(),
    state: usState.optional(),
    q: z.string().min(1).max(120).optional(),
    pipeline_stage: z.enum(PIPELINE_STAGES).optional(),
    relationship_owner_id: uuid.optional(),
    /** Refined model — filter to franchise (the blast network) or non-franchise dealers. */
    franchise: z
        .enum(['true', 'false'])
        .transform((v) => v === 'true')
        .optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
// ---------------------------------------------------------------------------
// M9 dealer recruitment cockpit — input DTOs (validated at the API edge)
// ---------------------------------------------------------------------------
/**
 * POST /api/dealers/:id/interactions — log a founder touch (the call-driven workflow). May carry an
 * inline follow-up reminder (follow_up_at + note) and optionally advance the dealer's pipeline_stage.
 */
export const createInteractionInput = z.object({
    type: z.enum(INTERACTION_TYPES),
    outcome: z.enum(INTERACTION_OUTCOMES).optional(),
    notes: z.string().max(5000).optional(),
    dealer_contact_id: uuid.optional(),
    occurred_at: isoTimestamp.optional(),
    follow_up_at: isoTimestamp.optional(),
    follow_up_note: z.string().max(2000).optional(),
    /** optional side effect: advance the dealer's recruitment stage as a result of this touch. */
    advance_stage: z.enum(PIPELINE_STAGES).optional(),
});
/** PATCH /api/dealer-interactions/:id — edit an interaction / mark its follow-up done. */
export const updateInteractionInput = z
    .object({
    outcome: z.enum(INTERACTION_OUTCOMES).nullable(),
    notes: z.string().max(5000).nullable(),
    follow_up_at: isoTimestamp.nullable(),
    follow_up_note: z.string().max(2000).nullable(),
    follow_up_done: z.boolean(),
})
    .partial()
    .refine((o) => Object.keys(o).length > 0, { message: 'at least one field is required' });
/**
 * POST /api/dealers/:id/live-iso — the <2-min live-demand capture. Same typed facets as an ISO pref
 * (so it is immediately usable by the matcher), plus: advance pipeline_stage → 'live_iso' (default on)
 * and optionally auto-log a 'captured_iso' interaction with the call note.
 */
export const captureLiveIsoInput = z
    .object({
    label: z.string().min(1).max(120),
    ...isoPrefBase,
    year_min: isoPrefBase.year_min.optional(),
    year_max: isoPrefBase.year_max.optional(),
    mileage_max: isoPrefBase.mileage_max.optional(),
    budget_max_cents: isoPrefBase.budget_max_cents.optional(),
    /** default true — move the dealer to pipeline_stage 'live_iso' on capture. */
    advance_stage: z.boolean().optional(),
    /** optional: auto-log a 'call' interaction (outcome 'captured_iso') carrying this note. */
    interaction_note: z.string().max(2000).optional(),
    /** optional: which contact the live demand came from (attached to the auto-logged interaction). */
    dealer_contact_id: uuid.optional(),
})
    .refine(yearOrder, { message: 'year_min must be <= year_max', path: ['year_min'] });
/**
 * One row of a founder's dealer-contact CSV. Idempotent on (business_name, state) for the dealer and
 * on the contact's phone/email — re-importing updates rather than duplicating. NO SMS consent captured
 * on import (consent is an explicit later operator step). Only dealership + state are required (a bare
 * prospect with no contact is a legitimate import); a contact is created only when phone/email present.
 */
export const dealerImportRowInput = z.object({
    dealership: z.string().min(1).max(200),
    state: usState,
    contact_name: z.string().min(1).max(200).optional(),
    phone_e164: e164.optional(),
    email: z.string().email().optional(),
    role_title: z.string().min(1).max(120).optional(),
    relationship_owner_name: z.string().min(1).max(120).optional(),
    preferred_contact_method: z.enum(PREFERRED_CONTACT_METHODS).optional(),
    notes: z.string().max(5000).optional(),
    // M15/M5 — identity disambiguators: name+state alone cannot separate two different "Sun Motors" FL.
    // A same-name-same-state row WITHOUT a separating disambiguator is flagged needs_review, never merged.
    city: z.string().min(1).max(120).optional(),
    external_ref: z.string().min(1).max(120).optional(),
    /** Refined model — mark the imported dealer as a Porsche franchise store (applies on CREATE only). */
    franchise: z.boolean().optional(),
});
/** POST /api/dealers/import — bulk import a founder's dealer contact list (dry-run by default). */
export const dealerImportInput = z.object({
    rows: z.array(dealerImportRowInput).min(1).max(1000),
    /** true (default) = validate + report what WOULD happen; false = actually write. */
    dry_run: z.boolean().default(true),
    /** stamp every imported dealer with this operator as relationship owner (rows may override by name). */
    relationship_owner_id: uuid.optional(),
});
//# sourceMappingURL=entities.js.map